/* ============================================================
   dbakers Cupcake Builder JS
   Prefix: dcb-

   - Tap flavor card → adds 1 cupcake (or shakes if box full)
   - Tap slot in box → removes that 1 cupcake
   - Free mix, no caps, all flavors available
   - Auto-fill: bestsellers (box of 6) or one-of-each (box of 12)

   ── 2026-06-01 — Dietary step removed ──
   The GF + Nut-Free toggles, the GF fee, the 3-cupcake grouping math,
   flavor lockouts, disclaimers and acknowledgments have all been
   removed. The builder is now a straight "fill your box → make it
   special" flow. Flavor data still carries gf-safe / nut-safe attrs
   (inert) and the markup still shows an informational "contains nuts"
   pill, but none of it gates anything here.

   ── 2026-05-21 — Cart-display fix ──
   Cart used to show the wrong price when addons were submitted in one
   fetch with the cupcake box, because the _addon_for property pointed
   to a manufactured string the cart-items template doesn't recognize.
   Two-step submit solves it (box first → key returned → addons tagged
   with key).

   ── 2026-05-27 — Anchored description popover ──
   The "?" description popover anchors next to the exact "?" button
   that was tapped (tooltip-style, with a caret). bindInfoButtons()
   passes the button element through, and openDescPopover() measures
   the button and positions the card via the --dcb-pop-top /
   --dcb-pop-left / --dcb-pop-arrow-left CSS variables + a
   data-placement of "above"/"below" (see the matching CSS).

   ADA: aria-live updates, keyboard support, focus management
   ============================================================ */

(function () {
  'use strict';

  if (!document.getElementById('dcb-root')) return;

  var CFG = window.dcbConfig || {};
  var BOX_SIZE = CFG.boxSize || 6;

  /* ── State ── */
  var state = {
    mode: 'pick',
    box: [],
    upsellDismissed: false
  };

  /* Cache flavor metadata from the DOM */
  var FLAVORS = {};
  document.querySelectorAll('.dcb-flavor-card').forEach(function (card) {
    var slug = card.dataset.flavorSlug;
    var name = card.dataset.flavor;
    if (slug && name) {
      FLAVORS[slug] = name;
    }
  });

  var standardPrice = CFG.standardPrice || 0;

  /* ── Init ── */
  function init() {
    bindSections();
    bindModeToggle();
    bindFlavorCards();
    bindInfoButtons();
    bindBoxClear();
    bindStickyBtn();
    bindUpsellClose();
    bindUpgrades();
    bindDescPopover();
    render();
  }

  if (document.readyState === 'complete') {
    setTimeout(init, 100);
  } else {
    window.addEventListener('load', function () { setTimeout(init, 100); });
  }

  /* ══════════════════════════════════════════════════════════════
     ── Accordion sections (1, 2) ──

     Behavior:
     - Click a section header → toggle open/closed for that section
     - Step 1 (Fill your box) open by default — the main work
     - Step 2 (Make it special) auto-opens when box hits BOX_SIZE

     Section header IDs:
       dcb-section-flavors-header  → body: dcb-section-flavors-body
       dcb-section-special-header  → body: dcb-section-special-body
  ══════════════════════════════════════════════════════════════ */

  /* Track auto-open history so we don't repeatedly auto-open the
     "Make it special" section if the customer closes it intentionally.
     Once auto-opened for a session, manual toggle takes over. */
  var autoOpenedSpecial = false;

  function bindSections() {
    var headers = document.querySelectorAll('.dcb-section-header');
    headers.forEach(function (header) {
      header.addEventListener('click', function () {
        var section = header.closest('.dcb-section');
        if (!section) return;
        var body = section.querySelector('.dcb-section-body');
        if (!body) return;
        var isOpen = body.classList.contains('is-open');
        toggleSection(section, !isOpen);
      });
    });
  }

  function toggleSection(sectionEl, shouldOpen) {
    var body = sectionEl.querySelector('.dcb-section-body');
    var header = sectionEl.querySelector('.dcb-section-header');
    if (!body || !header) return;
    body.classList.toggle('is-open', shouldOpen);
    header.setAttribute('aria-expanded', String(shouldOpen));
  }

  function openSection(id) {
    var section = document.getElementById(id);
    if (section) toggleSection(section, true);
  }

  function closeSection(id) {
    var section = document.getElementById(id);
    if (section) toggleSection(section, false);
  }

  /* ── Mode toggle (Pick / Auto-fill) ── */
  function bindModeToggle() {
    document.querySelectorAll('.dcb-mode-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var mode = this.dataset.mode;
        if (state.mode === mode) return;
        var prevMode = state.mode;
        state.mode = mode;
        updateModeButtons();
        if (mode === 'auto') {
          autoFillBox();
        } else if (mode === 'pick' && prevMode === 'auto') {
          state.box = [];
          announce('Box cleared. Pick your flavors.');
        }
        render();
      });
    });
  }

  function updateModeButtons() {
    document.querySelectorAll('.dcb-mode-btn').forEach(function (b) {
      var active = b.dataset.mode === state.mode;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-checked', String(active));
    });
  }

  function autoFillBox() {
    var ranking = BOX_SIZE === 6
      ? (CFG.bestsellers6 || [])
      : (CFG.oneOfEach12 || []);
    state.box = ranking.slice(0, BOX_SIZE);
    announce('Box auto-filled with ' + (BOX_SIZE === 6 ? 'bestsellers' : 'one of each flavor') + '. Tap a cupcake in your box to swap it.');
  }

  /* ── Flavor cards (tap to add) ── */
  function bindFlavorCards() {
    document.querySelectorAll('.dcb-flavor-card').forEach(function (card) {
      card.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        addFlavor(card);
      });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          addFlavor(card);
        }
      });
    });
  }

  function addFlavor(card) {
    var slug = card.dataset.flavorSlug;
    var name = card.dataset.flavor;
    if (!slug || !name) return;
    addFlavorStandard(card, slug, name);
  }

  /* ── Info (?) buttons on flavor cards (mobile only via CSS) ──
     stopPropagation is critical: without it, clicking ? would bubble
     to .dcb-flavor-card and add a cupcake when the customer just
     wanted to read the description. The tapped button is passed into
     openDescPopover so the popover can anchor next to that button. */
  function bindInfoButtons() {
    document.querySelectorAll('.dcb-flavor-info-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var desc = btn.dataset.desc || '';
        var name = btn.dataset.flavorName || '';
        openDescPopover(name, desc, btn);
      });
      btn.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          var desc = btn.dataset.desc || '';
          var name = btn.dataset.flavorName || '';
          openDescPopover(name, desc, btn);
        }
      });
    });
  }

  /* ── Description popover (mobile flavor details) ── */
  function bindDescPopover() {
    var pop = document.getElementById('dcb-desc-popover');
    if (!pop) return;

    /* CRITICAL: move the popover to be a direct child of <body>. The
       builder has ancestors with transform / backdrop-filter / animation
       (sticky bar, fade-ins, etc.), and ANY such ancestor makes a
       position:fixed descendant position relative to that ancestor instead
       of the viewport — which is why the card was shooting up into the
       image. Re-parenting to <body> guarantees fixed = viewport, so the
       card appears exactly where the tapped "?" is, in the current view. */
    if (pop.parentNode !== document.body) {
      document.body.appendChild(pop);
    }

    var closeBtn = document.getElementById('dcb-desc-popover-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', closeDescPopover);
    }
    /* Tap on the popover backdrop (the dimmed area outside the inner
       card) closes it. Tap on the inner card does not. */
    pop.addEventListener('click', function (e) {
      if (e.target === pop) closeDescPopover();
    });
    /* Esc to close */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !pop.hidden) closeDescPopover();
    });
  }

  /* Open the description popover anchored to the tapped "?" button.
     `btn` is the .dcb-flavor-info-btn that was activated. The card is
     measured after un-hiding (so offsetWidth/Height are real) and then
     positioned via CSS custom properties the stylesheet reads:
       --dcb-pop-top / --dcb-pop-left  → the card's top-left in px
       --dcb-pop-arrow-left            → the caret's x within the card
     plus data-placement = "above" | "below" for which edge the caret
     sits on. If no btn is supplied (defensive), it falls back to the
     CSS default (centered). */
  function openDescPopover(name, desc, btn) {
    var pop = document.getElementById('dcb-desc-popover');
    var titleEl = document.getElementById('dcb-desc-popover-title');
    var bodyEl = document.getElementById('dcb-desc-popover-body');
    if (!pop || !titleEl || !bodyEl) return;
    titleEl.textContent = name;
    bodyEl.textContent = desc;
    pop.hidden = false;

    var inner = pop.querySelector('.dcb-desc-popover-inner');
    if (btn && inner) {
      var b = btn.getBoundingClientRect();
      var gap = 10;   /* space between caret tip and the button */
      var pad = 12;   /* min margin from the screen edges */
      var cardW = inner.offsetWidth;
      var cardH = inner.offsetHeight;
      var vw = window.innerWidth;
      var vh = window.innerHeight;

      /* Horizontal: center the card on the button, clamp to viewport */
      var btnCenterX = b.left + b.width / 2;
      var left = btnCenterX - cardW / 2;
      left = Math.max(pad, Math.min(left, vw - cardW - pad));

      /* Caret keeps pointing at the button even after clamping */
      var arrowLeft = btnCenterX - left;
      arrowLeft = Math.max(16, Math.min(arrowLeft, cardW - 16));
      inner.style.setProperty('--dcb-pop-arrow-left', arrowLeft + 'px');

      /* Vertical: prefer above the button; flip below if no room;
         pin near the top as a last resort. (position:fixed coords,
         so no scrollY needed.) */
      var top, placement;
      if (b.top - cardH - gap >= pad) {
        top = b.top - cardH - gap;
        placement = 'above';
      } else if (b.bottom + cardH + gap <= vh - pad) {
        top = b.bottom + gap;
        placement = 'below';
      } else {
        top = pad;
        placement = 'above';
      }

      inner.style.setProperty('--dcb-pop-top', top + 'px');
      inner.style.setProperty('--dcb-pop-left', left + 'px');
      pop.setAttribute('data-placement', placement);
    }

    /* Focus the close button so a screen-reader user lands somewhere
       useful + Esc / Tab work intuitively. preventScroll keeps the
       focus from nudging the page, which would shift the anchor. */
    var closeBtn = document.getElementById('dcb-desc-popover-close');
    if (closeBtn) {
      try { closeBtn.focus({ preventScroll: true }); }
      catch (err) { closeBtn.focus(); }
    }
  }

  function closeDescPopover() {
    var pop = document.getElementById('dcb-desc-popover');
    if (pop) pop.hidden = true;
  }

  function addFlavorStandard(card, slug, name) {
    if (state.box.length >= BOX_SIZE) {
      shakeCard(card);
      announce('Your box is full. Tap a cupcake to remove it first, or try the ' + (CFG.otherBoxLabel || 'other box') + '.');
      maybeShowUpsell();
      return;
    }
    state.box.push(slug);
    render();
    announce(name + ' added. ' + state.box.length + ' of ' + BOX_SIZE + '.');
  }

  function maybeShowUpsell() {
    if (BOX_SIZE !== 6) return;
    if (state.upsellDismissed) return;
    var el = document.getElementById('dcb-upsell-banner');
    if (el) el.hidden = false;
  }

  function shakeCard(card) {
    card.classList.remove('is-shaking');
    void card.offsetWidth;
    card.classList.add('is-shaking');
    setTimeout(function () { card.classList.remove('is-shaking'); }, 400);
  }

  /* ── Box clear ── */
  function bindBoxClear() {
    var btn = document.getElementById('dcb-box-clear');
    if (!btn) return;
    btn.addEventListener('click', function () {
      if (state.box.length === 0) return;
      state.box = [];
      render();
      announce('Box cleared.');
    });
  }

  /* ── Box slot click (tap to remove) ── */
  function bindBoxSlot(slot, slotSlug, slotIndex) {
    function remove() {
      if (slotIndex < state.box.length) {
        var removed = state.box.splice(slotIndex, 1)[0];
        var rname = FLAVORS[removed] || 'Cupcake';
        render();
        announce(rname + ' removed. ' + state.box.length + ' of ' + BOX_SIZE + '.');
      }
    }
    slot.addEventListener('click', remove);
    slot.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        remove();
      }
    });
  }

  /* ── Sticky button (Add to cart) ── */
  function bindStickyBtn() {
    var btn = document.getElementById('dcb-sticky-btn');
    if (!btn) return;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (btn.disabled) return;
      submitCart();
    });
  }

  /* ── Upsell banner close button ── */
  function bindUpsellClose() {
    var btn = document.getElementById('dcb-upsell-close');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var el = document.getElementById('dcb-upsell-banner');
      if (el) el.hidden = true;
      state.upsellDismissed = true;
      announce('Upsell dismissed.');
    });
  }

  /* ── Upgrades (Make it special) — gift message + candles/sparklers ── */
  function bindUpgrades() {
    document.querySelectorAll('.dcb-addon-row').forEach(function (row) {
      row.addEventListener('click', function () {
        var card = row.closest('.dcb-addon-card');
        if (!card) return;
        var nowActive = !card.classList.contains('is-active');
        card.classList.toggle('is-active', nowActive);
        row.setAttribute('aria-expanded', String(nowActive));
        var panelId = row.getAttribute('aria-controls');
        if (panelId) {
          var panel = document.getElementById(panelId);
          if (panel) panel.hidden = !nowActive;
        }
        renderStickyBar();
      });
      row.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          row.click();
        }
      });
    });

    document.querySelectorAll('.dcb-type-pill').forEach(function (pill) {
      pill.addEventListener('click', function () {
        var on = pill.getAttribute('aria-checked') !== 'true';
        pill.setAttribute('aria-checked', String(on));
        pill.classList.toggle('is-active', on);
        var type = pill.dataset.type;
        var sub = document.getElementById('dcb-candle-sub-' + type);
        if (sub) sub.hidden = !on;
        renderStickyBar();
      });
    });

    document.querySelectorAll('.dcb-img-tile').forEach(function (tile) {
      tile.addEventListener('click', function () {
        var group = tile.dataset.tileGroup;
        if (!group) return;
        document.querySelectorAll('.dcb-img-tile[data-tile-group="' + group + '"]').forEach(function (t) {
          t.classList.remove('is-selected');
          t.setAttribute('aria-checked', 'false');
        });
        tile.classList.add('is-selected');
        tile.setAttribute('aria-checked', 'true');
        renderStickyBar();
      });
      tile.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          tile.click();
        }
      });
    });

    /* Number candle input — every digit is +1 candle, so the price
       has to recompute on each keystroke. */
    var numInput = document.getElementById('dcb-candle-number');
    if (numInput) {
      numInput.addEventListener('input', function () {
        renderStickyBar();
      });
    }
  }


  /* ── Render ── */
  function render() {
    renderBoxSlots();
    renderBoxCount();
    renderFlavorBadges();
    renderFlavorStates();
    renderClearBtn();
    renderStickyBar();
    renderUpsell();
    renderBoxHint();
    renderFlavorsSection();
    renderStepStates();
  }

  /* Step number badges:
     - Step 1 (Fill your box) → turns green ✓ when box is full
     - Step 2 (Make it special) → optional, no checkmark, not managed here

     Side-effect: when Step 1 becomes done for the first time, auto-open
     Step 2 so the customer sees the "make it special" options without
     scrolling. We only auto-open once (autoOpenedSpecial) so the
     customer can close it and have it stay closed. */
  function renderStepStates() {
    var boxFull = state.box.length === BOX_SIZE;

    var step1El = document.getElementById('dcb-step-num-1');
    if (step1El) {
      step1El.classList.toggle('is-done', boxFull);
      step1El.textContent = boxFull ? '✓' : '1';
    }

    /* Step 1 subtitle — shows progress as customer fills the box */
    var sub = document.getElementById('dcb-section-flavors-subtitle');
    if (sub) {
      if (boxFull) {
        sub.textContent = 'Box ready · ' + BOX_SIZE + ' cupcakes';
      } else {
        sub.textContent = state.box.length + ' of ' + BOX_SIZE + ' picked';
      }
    }

    /* Auto-open Step 2 (Make it special) the FIRST time box becomes full */
    if (boxFull && !autoOpenedSpecial) {
      autoOpenedSpecial = true;
      openSection('dcb-section-special');
      /* Scroll to step 2 so they see the new options. Small delay so
         the open animation can start first. */
      setTimeout(function () {
        var t = document.getElementById('dcb-section-special');
        if (t) t.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 250);
    }
  }

  function renderFlavorsSection() {
    var section = document.getElementById('dcb-flavors-section');
    if (!section) return;
    var ready = state.box.length === BOX_SIZE;
    section.classList.toggle('is-collapsed', ready);
    section.setAttribute('aria-hidden', ready ? 'true' : 'false');
  }

  function renderBoxSlots() {
    var slotsEl = document.getElementById('dcb-box-slots');
    if (!slotsEl) return;
    slotsEl.innerHTML = '';

    state.box.forEach(function (slug, index) {
      var card = document.querySelector('.dcb-flavor-card[data-flavor-slug="' + cssEscape(slug) + '"]');
      var slot = document.createElement('div');
      slot.className = 'dcb-box-slot is-filled';
      slot.tabIndex = 0;
      slot.setAttribute('role', 'button');
      var name = FLAVORS[slug] || 'Cupcake';
      slot.setAttribute('aria-label', name + ', tap to remove');

      if (card) {
        var imgWrap = card.querySelector('.dcb-flavor-img');
        if (imgWrap) {
          var bgColor = imgWrap.dataset.color || imgWrap.style.background || '';
          slot.style.setProperty('--dcb-frosting-color', bgColor);
          slot.style.background = '#fff';
          var img = imgWrap.querySelector('img');
          if (img) {
            var slotImg = document.createElement('img');
            slotImg.src = img.src;
            slotImg.alt = '';
            slot.appendChild(slotImg);
          } else {
            var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('class', 'dcb-cupcake-svg');
            svg.setAttribute('viewBox', '0 0 40 50');
            svg.setAttribute('aria-hidden', 'true');
            var frosting = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            frosting.setAttribute('class', 'dcb-cupcake-frosting');
            frosting.setAttribute('d', 'M8,18 Q8,8 20,8 Q32,8 32,18 Q32,22 30,24 L10,24 Q8,22 8,18 Z');
            frosting.setAttribute('fill', bgColor);
            var liner = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            liner.setAttribute('class', 'dcb-cupcake-liner');
            liner.setAttribute('d', 'M10,24 L30,24 L28,42 Q28,44 26,44 L14,44 Q12,44 12,42 Z');
            svg.appendChild(frosting);
            svg.appendChild(liner);
            slot.appendChild(svg);
          }
        }
      }

      bindBoxSlot(slot, slug, index);
      slotsEl.appendChild(slot);
    });

    for (var i = state.box.length; i < BOX_SIZE; i++) {
      var emptySlot = document.createElement('div');
      emptySlot.className = 'dcb-box-slot is-empty';
      emptySlot.setAttribute('aria-label', 'Empty slot ' + (i + 1));
      var emptySvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      emptySvg.setAttribute('class', 'dcb-empty-heart');
      emptySvg.setAttribute('viewBox', '0 0 24 24');
      emptySvg.setAttribute('aria-hidden', 'true');
      var heart = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      heart.setAttribute('d', 'M12 21s-7-4.5-9.5-9C1 9 2.5 5 6 5c2 0 3.5 1 4.5 2.5C11.5 6 13 5 15 5c3.5 0 5 4 3.5 7-2.5 4.5-9.5 9-9.5 9z');
      heart.setAttribute('fill', '#7a5791');
      heart.setAttribute('opacity', '0.18');
      emptySvg.appendChild(heart);
      emptySlot.appendChild(emptySvg);
      slotsEl.appendChild(emptySlot);
    }
  }

  function renderBoxCount() {
    var el = document.getElementById('dcb-box-count');
    if (!el) return;
    el.textContent = state.box.length + ' of ' + BOX_SIZE + ' cupcakes';
  }

  function renderBoxHint() {
    var el = document.getElementById('dcb-box-hint');
    if (!el) return;
    el.hidden = state.box.length === 0;
    el.textContent = 'Tap a cupcake to remove it from the box';
  }

  function renderFlavorBadges() {
    var counts = {};
    state.box.forEach(function (slug) {
      counts[slug] = (counts[slug] || 0) + 1;
    });
    document.querySelectorAll('.dcb-flavor-card').forEach(function (card) {
      var slug = card.dataset.flavorSlug;
      var n = counts[slug] || 0;
      var badge = card.querySelector('.dcb-flavor-badge');
      if (badge) {
        badge.textContent = n;
        badge.hidden = n === 0;
      }
      card.classList.toggle('is-selected', n > 0);
      card.setAttribute('aria-pressed', n > 0 ? 'true' : 'false');
    });
  }

  /* Render flavor states — cards disable when the box is full. No more
     GF / nut-free lockouts; the 🔒 icon stays hidden. */
  function renderFlavorStates() {
    var full = state.box.length >= BOX_SIZE;
    document.querySelectorAll('.dcb-flavor-card').forEach(function (card) {
      card.classList.toggle('is-disabled', full);
      var lockIcon = card.querySelector('.dcb-flavor-lock');
      if (lockIcon) lockIcon.hidden = true;
    });
  }

  function renderClearBtn() {
    var btn = document.getElementById('dcb-box-clear');
    if (btn) btn.disabled = state.box.length === 0;
  }

  /* Sum every active addon's price, in cents. Used by the sticky bar
     so the displayed total reflects what the customer will actually
     pay. */
  function calcAddonTotal() {
    var total = 0;

    var candleCard = document.getElementById('dcb-addon-card-candles');
    if (candleCard && candleCard.classList.contains('is-active')) {
      if (isPillOn('rainbow'))   total += (CFG.addonRainbowPrice || 0);
      if (isPillOn('sparklers')) total += (CFG.addonSparklersPrice || 0);
      if (isPillOn('metallic')) {
        var mTile = document.querySelector('#dcb-candle-sub-metallic .dcb-img-tile.is-selected');
        if (mTile) total += (parseInt(mTile.dataset.tilePrice, 10) || 0);
      }
      if (isPillOn('number')) {
        var nTile = document.querySelector('#dcb-candle-sub-number .dcb-img-tile.is-selected');
        var numInput = document.getElementById('dcb-candle-number');
        var nDigits = (numInput && numInput.value) ? numInput.value.replace(/\D/g, '') : '';
        if (nTile && nDigits) {
          total += (parseInt(nTile.dataset.tilePrice, 10) || 0) * nDigits.length;
        }
      }
    }

    return total;
  }

  function renderStickyBar() {
    var countEl = document.getElementById('dcb-sticky-count');
    var priceEl = document.getElementById('dcb-sticky-price');
    var btn     = document.getElementById('dcb-sticky-btn');

    var displayPrice = standardPrice + calcAddonTotal();
    var priceLabel = formatMoney(displayPrice);

    var ready = state.box.length === BOX_SIZE;
    var remaining = BOX_SIZE - state.box.length;
    var canSubmit = ready;

    if (countEl) {
      if (ready) {
        countEl.innerHTML = 'Box ready <span aria-hidden="true">✓</span>';
        countEl.classList.add('is-ready');
      } else if (remaining === 1) {
        countEl.innerHTML = '<strong>1 cupcake</strong> left';
        countEl.classList.remove('is-ready');
      } else {
        countEl.innerHTML = '<strong>' + remaining + '</strong> cupcakes left';
        countEl.classList.remove('is-ready');
      }
    }

    if (priceEl) {
      var sub = priceLabel;
      if (ready) {
        sub = BOX_SIZE + ' cupcakes · ' + priceLabel;
      } else {
        sub += ' · pick to continue';
      }
      priceEl.textContent = sub;
    }

    if (btn) {
      btn.disabled = !canSubmit;
      btn.textContent = 'Add to cart';
    }
  }

  function renderUpsell() {
    var el = document.getElementById('dcb-upsell-banner');
    if (!el) return;
    if (BOX_SIZE !== 6) {
      el.hidden = true;
      return;
    }
    var isFull = state.mode === 'pick' && state.box.length === BOX_SIZE;
    if (!isFull) {
      el.hidden = true;
      state.upsellDismissed = false;
    }
  }

  /* ══════════════════════════════════════════════════════════════
     ── Cart submit — two-step, mirrors signature cake builder ──
  ══════════════════════════════════════════════════════════════ */
  function submitCart() {
    var btn = document.getElementById('dcb-sticky-btn');
    if (btn) { btn.disabled = true; btn.textContent = 'Adding...'; }

    /* Step 1: cupcake box alone */
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: CFG.standardVariantId,
          quantity: 1,
          properties: buildBoxProperties()
        }]
      })
    })
    .then(function (r) {
      return r.json().then(function (data) {
        return { ok: r.ok, status: r.status, data: data };
      });
    })
    .then(function (res) {
      if (!res.ok) {
        if (btn) {
          btn.textContent = 'Add to cart';
          btn.disabled = !(state.box.length === BOX_SIZE);
        }
        console.error('dcb cupcake-add error:', res.status, res.data);
        var msg = (res.data && (res.data.description || res.data.message)) || 'Please try again.';
        alert('Something went wrong: ' + msg);
        return;
      }

      var cakeKey = res.data && res.data.items && res.data.items[0] && res.data.items[0].key
                  ? res.data.items[0].key
                  : null;
      var addons = buildAddonItems(cakeKey);

      if (!addons.length) {
        onCartSuccess(btn);
        return;
      }

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: addons })
      })
      .then(function (r) {
        return r.json().then(function (data) {
          return { ok: r.ok, status: r.status, data: data };
        });
      })
      .then(function (addonRes) {
        if (!addonRes.ok) {
          console.error('dcb addon-add error:', addonRes.status, addonRes.data);
          var amsg = (addonRes.data && (addonRes.data.description || addonRes.data.message)) || 'Some extras couldn\'t be added.';
          alert('Your cupcakes were added, but: ' + amsg + ' Please review your cart.');
          if (btn) btn.textContent = 'Added (with issues)';
          setTimeout(function () { window.location.href = '/cart'; }, 600);
          return;
        }
        onCartSuccess(btn);
      });
    })
    .catch(function (err) {
      console.error('dcb cart error:', err);
      if (btn) {
        btn.textContent = 'Add to cart';
        btn.disabled = !(state.box.length === BOX_SIZE);
      }
      alert('We couldn\'t reach the cart. Check your connection and try again.');
    });
  }

  /* Build properties for the cupcake box itself. */
  function buildBoxProperties() {
    var props = {};
    var counts = {};
    state.box.forEach(function (slug) {
      var name = FLAVORS[slug] || slug;
      counts[name] = (counts[name] || 0) + 1;
    });
    var parts = [];
    Object.keys(counts).forEach(function (name) {
      parts.push(name + ' ×' + counts[name]);
    });
    props['Flavors'] = parts.join(', ');

    if (state.mode === 'auto') {
      props['Selection'] = BOX_SIZE === 6 ? 'Bestsellers (assorted)' : '1 of each flavor';
    }

    /* Candles summary */
    var candleCard = document.getElementById('dcb-addon-card-candles');
    if (candleCard && candleCard.classList.contains('is-active')) {
      var candleParts = [];
      if (isPillOn('rainbow'))   candleParts.push('Rainbow candles');
      if (isPillOn('sparklers')) candleParts.push('Sparklers');
      if (isPillOn('metallic')) {
        var mTile = document.querySelector('#dcb-candle-sub-metallic .dcb-img-tile.is-selected');
        if (mTile) {
          var mColor = mTile.dataset.tileValue || '';
          candleParts.push('Metallic candles' + (mColor ? ' (' + mColor + ')' : ''));
        }
      }
      if (isPillOn('number')) {
        var nTile = document.querySelector('#dcb-candle-sub-number .dcb-img-tile.is-selected');
        var numInput = document.getElementById('dcb-candle-number');
        var nDigits = (numInput && numInput.value) ? numInput.value.replace(/\D/g, '') : '';
        if (nTile && nDigits) {
          var nColor = nTile.dataset.tileValue || '';
          candleParts.push('Number candles' + (nColor ? ' (' + nColor + ')' : '') + ' — #' + nDigits);
        }
      }
      if (candleParts.length) {
        props['Candles'] = candleParts.join(' + ');
      }
    }

    var giftCard = document.getElementById('dcb-addon-card-gift');
    if (giftCard && giftCard.classList.contains('is-active')) {
      var giftText = (document.getElementById('dcb-gift-text') || {}).value || '';
      giftText = giftText.trim();
      if (giftText) props['Gift message'] = giftText;
    }

    return props;
  }

  /* Build addon line items (candles / sparklers only). */
  function buildAddonItems(cakeKey) {
    var items = [];
    var tag = cakeKey ? { '_addon_for': cakeKey } : {};

    var candleCard = document.getElementById('dcb-addon-card-candles');
    if (candleCard && candleCard.classList.contains('is-active')) {
      var rainbowOn   = isPillOn('rainbow');
      var metallicOn  = isPillOn('metallic');
      var numberOn    = isPillOn('number');
      var sparklersOn = isPillOn('sparklers');

      if (rainbowOn && CFG.addonRainbowVariantId) {
        items.push({
          id: CFG.addonRainbowVariantId,
          quantity: 1,
          properties: Object.assign({ '_addon': 'Rainbow candles' }, tag)
        });
      }
      if (sparklersOn && CFG.addonSparklersVariantId) {
        items.push({
          id: CFG.addonSparklersVariantId,
          quantity: 1,
          properties: Object.assign({ '_addon': 'Sparklers' }, tag)
        });
      }
      if (metallicOn) {
        var mTile = document.querySelector('#dcb-candle-sub-metallic .dcb-img-tile.is-selected');
        if (mTile && mTile.dataset.tileVariantId) {
          var mColor = mTile.dataset.tileValue || '';
          items.push({
            id: parseInt(mTile.dataset.tileVariantId, 10),
            quantity: 1,
            properties: Object.assign({
              '_addon': 'Metallic candles' + (mColor ? ' (' + mColor + ')' : '')
            }, tag)
          });
        }
      }
      if (numberOn) {
        var nTile = document.querySelector('#dcb-candle-sub-number .dcb-img-tile.is-selected');
        var numInput = document.getElementById('dcb-candle-number');
        var nDigits = (numInput && numInput.value) ? numInput.value.replace(/\D/g, '') : '';
        if (nTile && nTile.dataset.tileVariantId && nDigits) {
          var nColor = nTile.dataset.tileValue || '';
          items.push({
            id: parseInt(nTile.dataset.tileVariantId, 10),
            quantity: nDigits.length,
            properties: Object.assign({
              'Number': nDigits,
              '_addon': 'Number candles' + (nColor ? ' (' + nColor + ')' : '')
            }, tag)
          });
        }
      }
    }

    return items;
  }

  function isPillOn(type) {
    var p = document.querySelector('.dcb-type-pill[data-type="' + type + '"]');
    return p && p.getAttribute('aria-checked') === 'true';
  }

  function onCartSuccess(btn) {
    if (btn) btn.textContent = 'Added! ✓';

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:change',  { bubbles: true }));

    setTimeout(function () { window.location.href = '/cart'; }, 400);
  }

  /* ── Helpers ── */
  function announce(msg) {
    var el = document.getElementById('dcb-sticky-live');
    if (el) el.textContent = msg;
  }

  function formatMoney(cents) {
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars;
  }

  function cssEscape(s) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(s);
    return String(s).replace(/[^a-zA-Z0-9_-]/g, function (c) {
      return '\\' + c.charCodeAt(0).toString(16) + ' ';
    });
  }

  /* ── Cart badge updater (shared with cake builders) ── */
  var EVENTS = ['cart:refresh', 'cart:change', 'cart:update', 'cart-update'];
  var lastCount = -1;
  function refreshBadge() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = 0;
        cart.items.forEach(function (item) {
          var isAddon = false;
          if (item.properties) {
            Object.keys(item.properties).forEach(function (key) {
              if (key === '_addon' || key === '_addon_for') isAddon = true;
            });
          }
          if (!isAddon) count += item.quantity;
        });
        if (count === lastCount) return;
        lastCount = count;
        document.querySelectorAll('.dbn-cart-badge').forEach(function (el) {
          el.textContent = count;
          el.style.display = count > 0 ? '' : 'none';
        });
        document.querySelectorAll('.cart-count-bubble span:not(.visually-hidden)').forEach(function (el) {
          el.textContent = count;
        });
      })
      .catch(function () { /* silent */ });
  }
  if (document.readyState === 'complete') refreshBadge();
  else window.addEventListener('load', refreshBadge);
  EVENTS.forEach(function (evt) { document.addEventListener(evt, refreshBadge); });

})();